import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import apiOffers from 'api/offers';
import IOfferGet from 'api/interfaces/offers/IOfferGet';
import { NDA_MASKED_VALUE } from 'services/enums/offers/ndaProtections';
import translations from 'services/translations/translations';

import { getMarginClass } from '../offerProduct/calculationWindow/getMarginRatioClassName';

import 'style/offers/LatestOrdersTicker.css';

// The orders shown, the latest first
const LATEST_ORDERS = 5;

/**
 * The latest ordered offers sliding along the title line of the offer list: the project,
 * its work types and city, the sale, the margin and the offerer (who signs the offer). A
 * click opens the offer; the pointer on the line stops it.
 */
const LatestOrdersTicker = (): JSX.Element | null => {
  const history = useHistory();
  const { t, i18n } = useTranslation([translations.offers]);
  const [orders, setOrders] = useState<IOfferGet[]>([]);

  useEffect(() => {
    let cancelled = false;
    apiOffers
      .get({
        collapseHistory: true,
        limit: LATEST_ORDERS,
        offset: 0,
        orderBy: 'orderedAt',
        orderByDirection: 'desc',
        // Ordered as a whole or as material
        status: 'approved,ordered-as-material',
      })
      .then((list) => {
        if (!cancelled) {
          // Those ordered before the times of ordering were recorded come last
          setOrders(list);
        }
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  if (orders.length === 0) {
    return null;
  }

  const money = (value: number) =>
    `${value.toLocaleString(i18n.language, { maximumFractionDigits: 0 })} €`;

  const items = orders.map((offer) => (
    <button
      className="latest-orders-item"
      key={`${offer.type}-${offer.id}`}
      onClick={() => history.push(`/offers/${offer.id}/${offer.type}`)}
      type="button"
    >
      <span className="latest-orders-name">
        {offer.targetName === NDA_MASKED_VALUE
          ? t(`${translations.offers}:nda.Protected`)
          : offer.targetName}
      </span>
      {offer.workTypes ? (
        <span className="latest-orders-detail">{offer.workTypes}</span>
      ) : null}
      {offer.city ? (
        <span className="latest-orders-detail">{offer.city}</span>
      ) : null}
      {offer.totalPrice != null ? <span>{money(offer.totalPrice)}</span> : null}
      {/* Pale green above 30 %, pale red below 20 %, as in the list */}
      {offer.marginPercent != null ? (
        <span
          className={`latest-orders-margin ${getMarginClass(
            offer.marginPercent
          )}`}
        >
          {`${offer.marginPercent.toLocaleString(i18n.language, {
            maximumFractionDigits: 1,
            minimumFractionDigits: 1,
          })} %`}
        </span>
      ) : null}
      {/* The offerer: who signs the offer */}
      {offer.signatureUserName ? (
        <span className="latest-orders-offerer">{offer.signatureUserName}</span>
      ) : null}
    </button>
  ));

  return (
    <div
      className="latest-orders"
      aria-label={t(`${translations.offers}:list.Latest orders`)}
    >
      <span className="latest-orders-label">
        {t(`${translations.offers}:list.Latest orders`)}
      </span>
      <div className="latest-orders-window">
        {/* Twice in a row, so the line slides on without a gap */}
        <div className="latest-orders-track">
          {items}
          <span aria-hidden="true" className="latest-orders-copy">
            {items}
          </span>
        </div>
      </div>
    </div>
  );
};

export default LatestOrdersTicker;
